import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { CheckCircle2, Clock, Eye, Gavel, Loader2, MinusCircle, Send, ThumbsUp, Trash2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import RichTextEditor from '@/components/ui/rich-text-editor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  APPEAL_KIND_LABELS,
  Appeal,
  AppealKind,
  AppealRoom,
  AppealSession,
  VERDICTS_TO_DECIDE,
  submitAppeal,
} from '../../../../services/appealsService';
import {
  AppealRequest,
  ClosedNotice,
  EmptyState,
  ErrorText,
  QuestionView,
  RichTextView,
  RoomHeader,
  cardCls,
  fieldCls,
  formatTime,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
} from './shared';

const KIND_OPTIONS: { value: AppealKind; icon: typeof ThumbsUp; hint: string }[] = [
  { value: 'removeQuestion', icon: MinusCircle, hint: 'Sual səhvdir, birmənalı deyil və ya qaydalara uyğun deyil.' },
  { value: 'acceptAnswer', icon: ThumbsUp, hint: 'Bizim cavabımız da düzgün sayılmalıdır.' },
];

/** An appeal is final, so the team confirms it in a dialog before it is sent. */
const AppealForm: React.FC<{ session: AppealSession }> = ({ session }) => {
  const [kind, setKind] = useState<AppealKind | null>(null);
  const [teamAnswer, setTeamAnswer] = useState('');
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  const review = (e: React.FormEvent) => {
    e.preventDefault();
    if (!kind) return setError('Apelyasiyanın nə üçün verildiyini seçin.');
    if (kind === 'acceptAnswer' && !teamAnswer.trim()) return setError('Komandanızın cavabını yazın.');
    if (!text.replace(/<[^>]*>/g, '').trim()) return setError('Apelyasiyanın mətnini yazın.');
    setError('');
    setConfirming(true);
  };

  const send = async () => {
    if (!kind) return;
    setSending(true);
    try {
      await submitAppeal(session, { kind, teamAnswer: kind === 'acceptAnswer' ? teamAnswer : '', text });
      toast.success('Apelyasiya göndərildi');
      setConfirming(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Apelyasiya göndərilmədi.');
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={review} className="space-y-5">
      <fieldset>
        <legend className={labelCls}>
          Apelyasiya nə üçündür? <span className="text-red-500">*</span>
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {KIND_OPTIONS.map(({ value, icon: Icon, hint }) => (
            <button
              key={value}
              type="button"
              onClick={() => setKind(value)}
              aria-pressed={kind === value}
              className={cn(
                'flex items-start gap-3 rounded-xl border-2 p-3.5 text-left transition-colors',
                kind === value
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-950/50'
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-800'
              )}
            >
              <Icon className={cn('mt-0.5 w-5 h-5 shrink-0', kind === value ? 'text-blue-600 dark:text-indigo-300' : 'text-gray-400')} />
              <span>
                <span className="block text-sm font-bold text-gray-900 dark:text-gray-50">{APPEAL_KIND_LABELS[value]}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{hint}</span>
              </span>
            </button>
          ))}
        </div>
      </fieldset>

      {kind === 'acceptAnswer' && (
        <div>
          <label htmlFor="team-answer" className={labelCls}>
            Komandanızın cavabı <span className="text-red-500">*</span>
          </label>
          <input
            id="team-answer"
            value={teamAnswer}
            onChange={e => setTeamAnswer(e.target.value)}
            maxLength={500}
            placeholder="Blankda yazdığınız cavab"
            className={fieldCls}
          />
        </div>
      )}

      <div>
        <span className={labelCls}>
          Apelyasiyanın mətni <span className="text-red-500">*</span>
        </span>
        <RichTextEditor
          value={text}
          onChange={setText}
          allowLinks
          placeholder="Arqumentlərinizi yazın. Mənbələri link kimi əlavə edə bilərsiniz…"
          className="[&_.ProseMirror]:min-h-[12rem]"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Mətni seçib link düyməsi ilə mənbə əlavə edin və ya linki birbaşa yapışdırın.
        </p>
      </div>

      {error && <ErrorText>{error}</ErrorText>}

      <div className="flex justify-end">
        <button type="submit" className={primaryBtnCls}>
          <Send className="w-4 h-4" />
          Göndər
        </button>
      </div>

      <Dialog open={confirming} onOpenChange={open => !sending && setConfirming(open)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Apelyasiyanı təsdiqləyin</DialogTitle>
            <DialogDescription>
              Bu suala yalnız bir apelyasiya vermək olar. Göndərildikdən sonra onu dəyişmək və ya geri götürmək mümkün
              olmayacaq.
            </DialogDescription>
          </DialogHeader>
          {kind && (
            <div className="rounded-xl bg-gray-50 dark:bg-gray-950 px-3 py-2.5 text-sm text-gray-800 dark:text-gray-200">
              <p className="font-bold">{APPEAL_KIND_LABELS[kind]}</p>
              {kind === 'acceptAnswer' && <p className="mt-1">Cavab: {teamAnswer.trim()}</p>}
            </div>
          )}
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setConfirming(false)} disabled={sending} className={secondaryBtnCls}>
              Geri qayıt
            </button>
            <button type="button" onClick={send} disabled={sending} className={primaryBtnCls}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Göndər
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
};

const decisionText = (appeal: Appeal) => {
  const accepted = appeal.decision === 'accepted';
  if (appeal.kind === 'removeQuestion') {
    return accepted
      ? 'Apelyasiya qəbul edildi: sual oyundan çıxarılır.'
      : 'Apelyasiya rədd edildi: sual oyunda qalır. Sualın çıxarılması üçün bütün jürilər razı olmalıdır.';
  }
  return accepted
    ? `Apelyasiya qəbul edildi: "${appeal.teamAnswer}" cavabı düzgün sayılır.`
    : 'Apelyasiya rədd edildi: cavabınız düzgün sayılmır.';
};

/** The final decision and every verdict, without juror names. Shown once three jurors have voted. */
const Decision: React.FC<{ appeal: Appeal }> = ({ appeal }) => {
  const accepted = appeal.decision === 'accepted';
  return (
    <div className="space-y-3">
      <div
        role="status"
        className={cn(
          'flex items-start gap-3 rounded-xl px-4 py-3',
          accepted
            ? 'bg-green-600 text-white'
            : 'bg-red-600 text-white'
        )}
      >
        {accepted ? <CheckCircle2 className="mt-0.5 w-5 h-5 shrink-0" /> : <XCircle className="mt-0.5 w-5 h-5 shrink-0" />}
        <div>
          <p className="text-base font-black">{accepted ? 'Qəbul edildi' : 'Rədd edildi'}</p>
          <p className="text-sm text-white/90">{decisionText(appeal)}</p>
        </div>
      </div>
      <div>
        <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-gray-900 dark:text-gray-50">
          <Gavel className="w-4 h-4" />
          Jürilərin rəyləri
        </p>
        <ul className="space-y-2">
          {appeal.verdicts.map((v, i) => (
            <li key={v.id} className="flex gap-2 rounded-xl border border-gray-100 dark:border-gray-800 px-3 py-2.5 text-sm">
              {v.accepted ? (
                <CheckCircle2 className="mt-0.5 w-4 h-4 shrink-0 text-green-500" />
              ) : (
                <XCircle className="mt-0.5 w-4 h-4 shrink-0 text-red-400" />
              )}
              <div className="min-w-0">
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  Jüri {i + 1}: {v.accepted ? 'bəli' : 'xeyr'}
                </span>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{v.text}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const STATUS = {
  decided: { label: 'Qərar verildi', icon: Gavel, cls: 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' },
  inReview: { label: 'Jürilər baxır', icon: Eye, cls: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300' },
  sent: { label: 'Göndərildi', icon: Clock, cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' },
} as const;

const SubmittedAppeal: React.FC<{ appeal: Appeal }> = ({ appeal }) => {
  const status = appeal.decision ? STATUS.decided : appeal.inReview ? STATUS.inReview : STATUS.sent;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-lg font-black text-gray-900 dark:text-gray-50">
          Sizin apelyasiyanız
          <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">{formatTime(appeal.updatedAt)}</span>
        </span>
        <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', status.cls)}>
          <status.icon className="w-3.5 h-3.5" />
          {status.label}
        </span>
      </div>
      {appeal.decision && <Decision appeal={appeal} />}
      <AppealRequest appeal={appeal} />
      <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 px-4 py-3">
        <RichTextView html={appeal.text} />
      </div>
      {!appeal.decision && (
        <p className="flex gap-2 rounded-xl bg-amber-50 dark:bg-amber-950/40 px-3.5 py-2.5 text-sm text-amber-800 dark:text-amber-200">
          <Eye className="mt-0.5 w-4 h-4 shrink-0" />
          <span>
            Apelyasiya qəbul olunub, onu dəyişmək və ya geri götürmək olmaz. {VERDICTS_TO_DECIDE} jüri səs verən
            kimi qərar burada görünəcək.
          </span>
        </p>
      )}
    </div>
  );
};

/** A team sees only the question its code belongs to, next to its appeal. */
const AppealTeamView: React.FC<{ room: AppealRoom; session: AppealSession; onLeave: () => void }> = ({
  room,
  session,
  onLeave,
}) => {
  const question = room.questions[0];
  const mine = room.appeals[0];

  return (
    <div className="space-y-4">
      <RoomHeader room={room} role="team" name={room.me} onLeave={onLeave} />

      {!room.isOpen && <ClosedNotice>Apelyasiya qəbulu bağlanıb.</ClosedNotice>}

      {!question ? (
        <EmptyState icon={Trash2}>Bu sual silinib. Təşkilatçıdan yeni kod istəyin.</EmptyState>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
          <div className={cn(cardCls, 'p-5 sm:p-6 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto')}>
            <QuestionView question={question} />
          </div>
          <div className={cn(cardCls, 'p-5 sm:p-6')}>
            {mine ? (
              <SubmittedAppeal appeal={mine} />
            ) : room.isOpen ? (
              <>
                <h2 className="mb-5 text-left text-lg font-black text-gray-900 dark:text-gray-50">Apelyasiya ver</h2>
                <AppealForm session={session} />
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">Apelyasiya verilməyib.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppealTeamView;
